import React, { useEffect, useRef, useState } from 'react';
import { storage } from '../firebaseConfig';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { db } from '../firebaseConfig';
import { collection, getDocs } from 'firebase/firestore';

function Clients() {
    const scrollRef = useRef(null);
    const [clientLogos, setClientLogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [scrollPosition, setScrollPosition] = useState(0);

    useEffect(() => {
        const fetchClientLogos = async () => {
            try {
                // Reference to the clients folder in Firebase Storage
                const clientsRef = ref(storage, 'clients');
                const result = await listAll(clientsRef);
                
                // Get download URLs for all images
                const logoPromises = result.items.map(async (item) => {
                    const url = await getDownloadURL(item);
                    return {
                        url,
                        name: item.name
                    };
                });
                
                const allLogos = await Promise.all(logoPromises);
                
                // Fetch visibility states from Firestore
                try {
                    const visibilityRef = collection(db, 'clientLogosVisibility');
                    const visibilitySnapshot = await getDocs(visibilityRef);
                    const visibilityData = {};
                    
                    visibilitySnapshot.forEach((doc) => {
                        visibilityData[doc.id] = doc.data().visible;
                    });
                    
                    // Filter logos to only show visible ones
                    const visibleLogos = allLogos.filter(logo => 
                        visibilityData[logo.name] !== undefined ? visibilityData[logo.name] : true
                    );
                    
                    setClientLogos(visibleLogos);
                    
                    // Check if we should enable auto-scroll (more than 6 visible logos)
                    setShouldAutoScroll(visibleLogos.length > 6);
                } catch (error) {
                    console.error('Error fetching visibility states:', error);
                    // If Firestore fails, show all logos
                    setClientLogos(allLogos);
                    setShouldAutoScroll(allLogos.length > 6);
                }
            } catch (error) {
                console.error('Error fetching client logos:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchClientLogos();
    }, []);

    useEffect(() => {
        const scrollContainer = scrollRef.current;
        if (!scrollContainer || !shouldAutoScroll || isPaused) {
            return;
        }
        
        // Start from stored position if resuming
        let scrollAmount = scrollPosition;
        const scrollSpeed = 0.8; // Slightly slower for smoother movement
        const intervalTime = 16; // 60fps for smoother animation

        const scroll = () => {
            if (scrollContainer.scrollWidth > scrollContainer.clientWidth) {
                scrollAmount += scrollSpeed;
                
                const maxScroll = scrollContainer.scrollWidth - scrollContainer.clientWidth;
                const logoWidth = 100 + 40; // logo height + gap (updated for 100px height)
                const resetPoint = maxScroll - logoWidth; // Reset before reaching the end
                
                if (scrollAmount >= resetPoint) {
                    scrollAmount = 0; // Reset to beginning seamlessly
                }
                
                scrollContainer.scrollLeft = scrollAmount;
                setScrollPosition(scrollAmount); // Store current position
            } else {
                scrollAmount = 0; // No need to scroll if content fits
            }
        };
        


        const interval = setInterval(scroll, intervalTime);

        return () => clearInterval(interval);
    }, [clientLogos, shouldAutoScroll, isPaused, scrollPosition]); // Re-run when clientLogos, shouldAutoScroll, isPaused, or scrollPosition changes

    if (loading) {
        return (
            <div className="clients" style={{
                padding: '20px 0',
                background: '#f8f9fa',
                overflow: 'hidden'
            }}>
                <div className="clients-scroll-container" ref={scrollRef} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80px'
                }}>
                    <div className="loading-logos" style={{
                        color: '#6c757d',
                        fontSize: '16px',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: '32px',
                            height: '32px',
                            border: '3px solid #e9ecef',
                            borderTop: '3px solid #4D92CE',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite',
                            margin: '0 auto 12px'
                        }}></div>
                        Loading client logos...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="clients" style={{
            padding: '20px 0',
            background: '#f8f9fa',
            overflow: 'hidden',
            position: 'relative'
        }}>
            <div 
                className="clients-scroll-container" 
                ref={scrollRef}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '40px',
                    padding: '0 20px',
                    minHeight: '80px',
                    overflow: shouldAutoScroll ? 'hidden' : 'auto',
                    whiteSpace: 'nowrap',
                    position: 'relative',
                    willChange: shouldAutoScroll ? 'scroll-position' : 'auto'
                }}
                onMouseEnter={() => {
                    setIsPaused(true);
                    // Store current scroll position when pausing
                    if (scrollRef.current) {
                        setScrollPosition(scrollRef.current.scrollLeft);
                    }
                }}
                onMouseLeave={() => {
                    setIsPaused(false);
                    // Resume from stored position
                    if (scrollRef.current && scrollPosition > 0) {
                        scrollRef.current.scrollLeft = scrollPosition;
                    }
                }}
            >
                {/* Main set of logos with proper sizing */}
                {clientLogos.map((logo, index) => (
                    <img 
                        key={`logo-${index}`}
                        src={logo.url} 
                        alt={`Client Logo ${index + 1}`}
                        className="client-logo"
                        style={{
                            height: '100px',
                            width: 'auto',
                            filter: 'grayscale(100%)',
                            objectFit: 'contain',
                            flexShrink: 0,
                            transition: 'filter 0.3s ease',
                            cursor: 'pointer',
                            minWidth: '80px' // Ensure minimum width
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.filter = 'grayscale(0%)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.filter = 'grayscale(100%)';
                        }}
                    />
                ))}
                
                {/* Only add duplicate set if we have many logos and need seamless scroll */}
                {shouldAutoScroll && clientLogos.length > 8 && clientLogos.map((logo, index) => (
                    <img 
                        key={`duplicate-${index}`}
                        src={logo.url} 
                        alt={`Client Logo ${index + 1}`}
                        className="client-logo"
                        style={{
                            height: '100px',
                            width: 'auto',
                            filter: 'grayscale(100%)',
                            objectFit: 'contain',
                            flexShrink: 0,
                            transition: 'filter 0.3s ease',
                            cursor: 'pointer',
                            minWidth: '80px' // Ensure minimum width
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.filter = 'grayscale(0%)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.filter = 'grayscale(100%)';
                        }}
                    />
                ))}
            </div>
            
            {/* Add gradient overlays for smooth edges when auto-scrolling */}
            {shouldAutoScroll && (
                <>
                    <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: '60px',
                        background: 'linear-gradient(to right, #f8f9fa 0%, transparent 100%)',
                        pointerEvents: 'none',
                        zIndex: 1
                    }}></div>
                    <div style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '60px',
                        background: 'linear-gradient(to left, #f8f9fa 0%, transparent 100%)',
                        pointerEvents: 'none',
                        zIndex: 1
                    }}></div>
                </>
            )}

            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}

export default Clients;